import { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from '@praktio/common-ui';
import classNames from 'classnames';
import { Container, Row, Col, Card, CardColumns } from 'react-bootstrap';
import { orderBy } from 'lodash';

import './CourseInfo.scss';
import PageHeader from '../common/PageHeader/PageHeader';
import { generateCLEString, processModuleAvailability } from '../../utils/functions';
import { WeDontShareYourScoresBody } from '../../utils/constants';
import { CourseCollaboration } from '../common/CourseCollaboration/CourseCollaboration';
import { useEffect } from 'react';

interface ICourseInfoDetailCardProps {
  title: string;
  body: JSX.Element | string | undefined;
}

interface IProps {
  course: ICourse;
}

const renderDetailCard = (card: ICourseInfoDetailCardProps) => {
  if (card.body) {
    return (
      <Card className="CourseInfo-detail-card">
        <Card.Body>
          <Card.Title>
            <span>{card.title}</span>
          </Card.Title>
          <Card.Text>
            <span>{card.body}</span>
          </Card.Text>
        </Card.Body>
      </Card>
    );
  }
  return;
};

const CourseInfo = ({ course }: IProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    modules,
    title,
    logo,
    subtitle,
    description,
    expiration,
    cleCredits,
    duration,
    hideScoreBlock,
    authoredBy,
    blocks,
    collaboration,
    isUnlocked,
  } = course;

  const { expiration: expirationDate } = expiration || {};
  const accessExpirationCardData: ICourseInfoDetailCardProps = {
    title: 'Access Expiration',
    body: expirationDate
      ? `Your access to this course will expire on ${new Date(
          expirationDate,
        ).toLocaleDateString()}.`
      : undefined,
  };

  const cleCreditsCardData: ICourseInfoDetailCardProps = {
    title: 'CLE',
    body:
      cleCredits && cleCredits.length > 0
        ? generateCLEString(cleCredits, authoredBy, isUnlocked)
        : 'Not currently available.',
  };

  const compatibilityCardData: ICourseInfoDetailCardProps = {
    title: 'Compatibility',
    body: 'Use Chrome or Edge on a PC or Mac computer.',
  };

  const hourString = duration === 1 ? 'hour' : 'hours';

  const durationCardData: ICourseInfoDetailCardProps = {
    title: 'Duration',
    body: duration
      ? `On average, it takes about ${duration} ${hourString} to complete this course.`
      : undefined,
  };

  const savedProgressCardData: ICourseInfoDetailCardProps = {
    title: 'Saved Progress',
    body: "Your progress in this course will automatically save, allowing you to build on what you've already completed when you return.",
  };

  const dontShareScoresCardData: ICourseInfoDetailCardProps = {
    title: "We Don't Share Your Scores!",
    body: !hideScoreBlock ? WeDontShareYourScoresBody : undefined,
  };

  let cardsToRender: ICourseInfoDetailCardProps[] = [];

  // Create an array of standard detail cards.
  let detailCards = [
    accessExpirationCardData,
    cleCreditsCardData,
    compatibilityCardData,
    durationCardData,
    savedProgressCardData,
    dontShareScoresCardData,
  ];

  // Add to cardsToRender if there is a valid body
  detailCards.forEach((detailCard) => {
    if (detailCard.body) {
      cardsToRender.push(detailCard);
    }
  });

  // Add custom blocks to the array if there are any present
  if (blocks) {
    blocks.forEach((block) => {
      cardsToRender.push({
        title: block.label,
        body: block.content,
      });
    });
  }

  const alphabetizedCardsToRender = orderBy(cardsToRender, ['title'], ['asc']);

  const [downloadDisabled, setDownloadDisabled] = useState(false);
  const courseId = course._id as string;
  let requiredModuleCount = 0;
  let requiredModulesComplete = 0;
  modules?.forEach((m: IModule) => {
    if (!m.isOptional) {
      requiredModuleCount += 1;
      if (m.completed) {
        requiredModulesComplete += 1;
      }
    }
  });

  const percentage = requiredModuleCount
    ? Math.floor((requiredModulesComplete / requiredModuleCount) * 100)
    : 0;

  const moduleStarted = !!modules?.find((m) => m.started);
  const moduleAvailability = processModuleAvailability(course);
  const incompleteRequiredModule = moduleAvailability.find((ma) => {
    const { module, isAvailable } = ma;
    if (!isAvailable) {
      return false;
    }

    const { completed, isOptional, timeReleasePeriod, isReleased } = module;
    return !completed && !isOptional && (!timeReleasePeriod || isReleased);
  })?.module;

  const moduleTitle =
    moduleStarted && incompleteRequiredModule
      ? incompleteRequiredModule.title
      : !moduleStarted && modules?.length
      ? modules[0].title
      : undefined;

  // const showStartOrContinueButton = modules?.length && (!moduleStarted || !!incompleteRequiredModule);
  const showStartButton = modules?.length && !moduleStarted;

  const contentRef = useRef<HTMLDivElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const [contentOverflowing, setContentOverflowing] = useState(false);

  const handleResize = () => {
    if (
      contentRef.current &&
      contentWrapperRef.current &&
      contentWrapperRef.current.offsetHeight > contentRef.current.offsetHeight
    ) {
      setContentOverflowing(true);
    } else {
      setContentOverflowing(false);
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <div className="CourseInfo">
        <PageHeader
          pageName={title || 'Unnamed Course'}
          subtitle={subtitle}
          logo={logo}
          isCourseInfoPage
        ></PageHeader>

        <Container
          ref={contentRef}
          className={classNames('CourseInfo-content px-lg-45 px-4')}
          fluid
        >
          <div ref={contentWrapperRef} className="CourseInfo-content-wrapper">
            {!!description && (
              <Row>
                <Col xs={12}>
                  <div
                    className="CourseInfo-description text-left"
                    dangerouslySetInnerHTML={{ __html: description || '' }}
                  ></div>
                </Col>
              </Row>
            )}
            <Row className="CourseInfo-cards-row">
              <Col>
                <CardColumns>
                  {alphabetizedCardsToRender.map((card) => renderDetailCard(card))}
                </CardColumns>
              </Col>
            </Row>

            <div className={classNames('CourseInfo-spacer', { 'no-collab': !collaboration })}></div>

            {/* Collaboration Logo */}
            {!!collaboration && <CourseCollaboration fileSource={collaboration as string} />}

            {/* Start Module Button */}
            {!!showStartButton && (
              <div
                className={classNames('CourseInfo-start', {
                  'space-for-scrollbar': contentOverflowing,
                })}
              >
                <Button
                  className="CourseInfo-button"
                  onClick={() => {
                    if (!moduleStarted && modules?.length) {
                      const moduleId = modules[0]._id;
                      navigate(`/courses/${courseId}/modules/${moduleId}`);
                    }
                    // We are temporarily removing the continue button
                    // else if(moduleStarted && incompleteRequiredModule){
                    //   const moduleId = incompleteRequiredModule._id;
                    //   navigate(`/courses/${courseId}/modules/${moduleId}`);
                    // }
                  }}
                  btnStyle="primary"
                  tooltip={moduleTitle}
                  tooltipPlacement="top"
                >
                  {moduleStarted ? 'CONTINUE' : 'START'}
                </Button>
              </div>
            )}
          </div>
        </Container>
      </div>
    </>
  );
};

export default CourseInfo;
